import { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { s3 } from "../../resources/config";
import classes from "./PopupModal.module.css";
import icons, { iconsAlts } from "../../resources/icons";

const DONT_SHOW_AGAIN_KEY = (id) => `vf_popup_dismissed_${id}`;
const CLICKED_KEY = (id) => `vf_popup_clicked_${id}`;
const COUNT_KEY = (id) => `vf_popup_count_${id}`;

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return s3 + url;
};

const isEligible = (popup) => {
  if (localStorage.getItem(DONT_SHOW_AGAIN_KEY(popup.id))) return false;
  if (localStorage.getItem(CLICKED_KEY(popup.id))) return false;
  const count = parseInt(localStorage.getItem(COUNT_KEY(popup.id)) || "0", 10);
  const maxCount = Math.max(popup.max_show_count ?? 1, 1); // guard against negative/absent values
  return count < maxCount;
};

const PopupModal = () => {
  const popups = useSelector((state) => state.general.webPopups);

  // Queue of eligible popup objects, built once when data arrives.
  const queueRef = useRef([]);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  // Build queue and schedule the first popup when the array arrives from Redux.
  // Guard with a ref so re-renders don't rebuild the queue.
  const initializedRef = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!Array.isArray(popups) || popups.length === 0) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    queueRef.current = popups.filter(isEligible);
    scheduleNext(0);

    return () => clearTimeout(timerRef.current);
  }, [popups]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleNext = (queueIndex) => {
    const queue = queueRef.current;
    if (!queue || queueIndex >= queue.length) return;

    const popup = queue[queueIndex];
    const delayMs = (popup.show_delay_seconds ?? 3) * 1000;

    timerRef.current = setTimeout(() => {
      const showPopup = () => {
        setCurrentPopup(popup);
        setVisible(true);
        const count = parseInt(localStorage.getItem(COUNT_KEY(popup.id)) || "0", 10);
        localStorage.setItem(COUNT_KEY(popup.id), String(count + 1));
      };

      const imageUrl = resolveImageUrl(popup.image?.url);
      if (imageUrl) {
        const img = new Image();
        img.onload = showPopup;
        img.onerror = showPopup; // show anyway if image fails
        img.src = imageUrl;
      } else {
        showPopup();
      }
    }, delayMs);
  };

  const advanceQueue = () => {
    const queue = queueRef.current;
    if (!queue || !currentPopup) return;
    const nextIndex = queue.findIndex((p) => p.id === currentPopup.id) + 1;
    setVisible(false);
    setCurrentPopup(null);
    if (nextIndex < queue.length) {
      scheduleNext(nextIndex);
    }
  };

  const dontShowAgain = (e) => {
    e.stopPropagation();
    if (!currentPopup) return;
    localStorage.setItem(DONT_SHOW_AGAIN_KEY(currentPopup.id), "true");
    advanceQueue();
  };

  const close = (e) => {
    e?.stopPropagation();
    advanceQueue();
  };

  const handleKeyDown = (handler) => (e) => {
    if (e.key === "Enter" || e.key === " ") handler(e);
  };

  // Focus the close button when the popup first becomes visible.
  useEffect(() => {
    if (visible && containerRef.current) {
      const firstBtn = containerRef.current.querySelector("button");
      firstBtn?.focus();
    }
  }, [visible]);

  // Trap keyboard focus inside the dialog while it is open.
  const handleContainerKeyDown = (e) => {
    if (e.key === "Tab") {
      const focusable = Array.from(
        containerRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      return;
    }
    if (e.key === "Enter" || e.key === " ") openLink(e);
  };

  const openLink = () => {
    if (!currentPopup) return;
    // Permanently suppress the popup once the user actively engages with it.
    localStorage.setItem(CLICKED_KEY(currentPopup.id), "true");
    if (currentPopup.link_url) {
      window.open(currentPopup.link_url, "_blank", "noopener,noreferrer");
    }
    advanceQueue();
  };

  if (!currentPopup) return null;

  const altText = currentPopup.image?.alternativeText || "";
  const imageUrl = resolveImageUrl(currentPopup.image?.url);

  return (
    <Modal show={visible} contentClassName={classes.mainContainer} onHide={close} centered>
      
      <div ref={containerRef} className={classes.contentContainer} onClick={openLink} role="button" tabIndex={0} onKeyDown={handleContainerKeyDown}>
        <button
        className={classes.closeButton}
        onClick={close}
        onKeyDown={handleKeyDown(close)}
        aria-label={t("close")}
      >
        <img src={icons.closeXWhite} alt={iconsAlts.closeXWhite} />
      </button>
        {imageUrl && (
          <img className={classes.popupImage} src={imageUrl} alt={altText} />
        )}
        {currentPopup.message && (
          <p className={classes.popupMessage}>{currentPopup.message}</p>
        )}
        <div className={classes.dismissBar}>
          <button
            className={classes.ctaButton}
            onClick={(e) => { e.stopPropagation(); openLink(); }}
            onKeyDown={handleKeyDown((e) => { openLink(); })}
          >
            {currentPopup.button_text}
          </button>
          <button
            className={classes.dismissButton}
            onClick={dontShowAgain}
            onKeyDown={handleKeyDown(dontShowAgain)}
          >
            {t("popup_dont_show_again")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PopupModal;
